import { Image } from 'expo-image';
import React, { useMemo } from 'react';
import { Modal, Pressable, ScrollView, View, useWindowDimensions } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { AppText } from '@/components/ui/AppText';
import { useThemePalette } from '@/config/theme';
import type { MatchPlayer } from '@/lib/data/provider';
import type { Json } from '@/lib/supabase/types';
import { localizeDigits } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';

// ---------------------------------------------------------------------------
// Defensive reader: `raw` is the provider's verbatim per-player blob, so a key
// may be absent, nested, or the wrong type. Returns null when unsure.
// ---------------------------------------------------------------------------
function rawNum(raw: Json | null, ...path: string[]): number | null {
  let node: unknown = raw;
  for (const key of path) {
    if (!node || typeof node !== 'object' || Array.isArray(node)) return null;
    node = (node as Record<string, unknown>)[key];
  }
  return typeof node === 'number' && Number.isFinite(node) ? node : null;
}

/** First non-null among several `raw` lookups (top-level OR nested fallbacks). */
function firstRawNum(raw: Json | null, paths: string[][]): number | null {
  for (const path of paths) {
    const value = rawNum(raw, ...path);
    if (value !== null) return value;
  }
  return null;
}

function oneDecimal(value: number): string {
  return (Math.round(value * 10) / 10).toFixed(1);
}
function twoDecimals(value: number): string {
  return (Math.round(value * 100) / 100).toFixed(2);
}

/**
 * Rating chip tint per the panel's scale (mirrors XgPlayerRatings): ≥7.5 green /
 * 6.5–7.5 neutral accent / <6.5 dim. Palette-aware so it reads on light + dark.
 */
function ratingTone(rating: number, palette: ReturnType<typeof useThemePalette>) {
  if (rating >= 7.5) return { bg: palette.win, fg: '#04140B' };
  if (rating >= 6.5) return { bg: palette.voltGlassStrong, fg: palette.voltBright };
  return { bg: palette.surface2, fg: palette.muted };
}

/** Two-letter initials from a player's name, for the no-photo fallback. */
function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase();
}

/**
 * Circular header headshot. Falls back to the player's initials on a neutral
 * surface when the URL is null or the image fails to load.
 */
function HeaderPhoto({ uri, name, ring }: { uri: string | null; name: string; ring: string }) {
  const [failed, setFailed] = React.useState(false);
  const showPhoto = Boolean(uri) && !failed;
  const SIZE = 56;

  return (
    <View
      className="items-center justify-center overflow-hidden rounded-full bg-surface-2"
      style={{ width: SIZE, height: SIZE, borderWidth: 1.5, borderColor: ring }}
    >
      {showPhoto ? (
        <Image
          source={{ uri: uri as string }}
          style={{ width: SIZE, height: SIZE }}
          contentFit="cover"
          contentPosition="top"
          transition={150}
          cachePolicy="memory-disk"
          onError={() => setFailed(true)}
          accessibilityLabel={name}
        />
      ) : (
        <AppText variant="heading" weight="bold" className="text-soft">
          {initialsOf(name)}
        </AppText>
      )}
    </View>
  );
}

/** A stat tile: big value over a small label (mirrors the season grid look). */
function StatCell({ label, value }: { label: string; value: string }) {
  return (
    <View className="min-w-[88px] flex-1 items-center gap-1 rounded-card bg-surface-2/60 px-2 py-3">
      <AppText variant="heading" weight="bold" tabular className="text-ink">
        {value}
      </AppText>
      <AppText variant="micro" className="text-center text-muted" numberOfLines={2}>
        {label}
      </AppText>
    </View>
  );
}

/** A single label/value row in the detail strip. */
function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <View className="flex-row items-center justify-between py-2">
      <AppText variant="caption" className="text-soft" numberOfLines={1}>
        {label}
      </AppText>
      <AppText variant="caption" weight="bold" tabular className="text-ink">
        {value}
      </AppText>
    </View>
  );
}

export interface PlayerDetailSheetProps {
  /** The tapped player's match line; null closes the sheet. */
  player: MatchPlayer | null;
  onClose: () => void;
}

/**
 * A bottom-sheet player MATCH-performance card, opened by tapping a player in the
 * analytics ratings list. It renders entirely from the tapped player's MatchPlayer
 * row (no network call): a header (photo + name + position + a colour-scaled rating
 * badge), a stat grid (minutes, goals, assists, xG, xA, shots, on-target, key passes,
 * pass accuracy), and a detail strip read defensively from the provider's `raw`
 * blob (duels won, tackles, interceptions, clearances, recoveries, touches) — each
 * line shown only when present. Reuses the app's Modal bottom-sheet pattern.
 */
export function PlayerDetailSheet({ player, onClose }: PlayerDetailSheetProps) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { arabicNumerals } = useLocale();
  const insets = useSafeAreaInsets();
  const { height: screenH } = useWindowDimensions();

  const open = player !== null;
  const num = (value: number) => localizeDigits(String(value), arabicNumerals);
  const sideColor = player?.team_side === 'away' ? palette.teamAway : palette.volt;

  // Headline stat grid: only tiles whose value is meaningful are shown.
  const tiles = useMemo(() => {
    if (!player) return [] as { label: string; value: string }[];
    const cells: { label: string; value: string }[] = [];
    const passAccuracy =
      player.total_passes > 0
        ? `${localizeDigits(
            String(Math.round((player.accurate_passes / player.total_passes) * 100)),
            arabicNumerals,
          )}%`
        : null;

    cells.push({ label: t('match.playerStats.minutes'), value: num(player.minutes) });
    if (player.goals > 0) cells.push({ label: t('match.playerStats.goals'), value: num(player.goals) });
    if (player.assists > 0)
      cells.push({ label: t('match.playerStats.assists'), value: num(player.assists) });
    cells.push({
      label: t('match.xgShort'),
      value: localizeDigits(twoDecimals(player.xg), arabicNumerals),
    });
    cells.push({
      label: t('match.ratingXa'),
      value: localizeDigits(twoDecimals(player.xa), arabicNumerals),
    });
    cells.push({
      label: t('match.ratingShots'),
      value: `${num(player.shots_on_target)}/${num(player.total_shots)}`,
    });
    cells.push({ label: t('match.ratingKeyPasses'), value: num(player.key_passes) });
    if (passAccuracy) cells.push({ label: t('match.ratingPassAccuracy'), value: passAccuracy });
    return cells;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player, t, arabicNumerals]);

  // Detail strip from `raw`: each line present only when the provider supplied it.
  const detailRows = useMemo(() => {
    if (!player) return [] as { label: string; value: string }[];
    const rows: { label: string; value: string }[] = [];
    const push = (label: string, value: number | null) => {
      if (value !== null) rows.push({ label, value: num(value) });
    };
    push(t('match.ratingDuelsWon'), firstRawNum(player.raw, [['duels', 'total_duels_won'], ['duel_won']]));
    push(
      t('match.ratingTackles'),
      firstRawNum(player.raw, [['defending', 'total_tackles'], ['total_tackle'], ['defending', 'tackles']]),
    );
    push(t('match.ratingInterceptions'), firstRawNum(player.raw, [['interception'], ['interceptions']]));
    push(t('match.ratingClearances'), firstRawNum(player.raw, [['total_clearance'], ['clearances']]));
    push(t('match.ratingRecoveries'), firstRawNum(player.raw, [['ball_recovery'], ['recoveries']]));
    push(t('match.ratingTouches'), firstRawNum(player.raw, [['touches']]));
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player, t, arabicNumerals]);

  if (!player) return <Modal visible={false} transparent onRequestClose={onClose} />;

  const rating = player.rating;
  const tone = rating != null ? ratingTone(rating, palette) : null;
  const positionLabel = player.position
    ? t(`player.positions.${player.position}`, { defaultValue: player.position })
    : null;

  return (
    <Modal visible={open} animationType="slide" transparent onRequestClose={onClose}>
      <Pressable className="flex-1 justify-end bg-black/60" onPress={onClose}>
        <Pressable
          onPress={(event) => event.stopPropagation()}
          className="rounded-t-3xl border border-line bg-surface"
          style={{ paddingBottom: insets.bottom + 16, maxHeight: screenH * 0.88 }}
        >
          {/* Grab handle */}
          <View className="items-center pt-3">
            <View className="h-1 w-10 rounded-full bg-line" />
          </View>

          {/* Header: photo + name + position + rating badge. */}
          <View className="flex-row items-center gap-3 px-5 pb-4 pt-3">
            <HeaderPhoto uri={player.photo_url} name={player.player_name} ring={sideColor} />
            <View className="flex-1">
              <AppText variant="heading" weight="bold" className="text-ink" numberOfLines={1}>
                {player.player_name}
              </AppText>
              {positionLabel ? (
                <AppText variant="micro" className="mt-0.5 text-muted" numberOfLines={1}>
                  {positionLabel}
                  {player.started ? `  ·  ${t('match.playerStats.starter')}` : ''}
                </AppText>
              ) : null}
            </View>
            {tone ? (
              <View
                className="items-center justify-center rounded-lg px-3 py-1.5"
                style={{ backgroundColor: tone.bg }}
              >
                <AppText weight="bold" tabular style={{ color: tone.fg, fontSize: 18 }}>
                  {localizeDigits(oneDecimal(rating as number), arabicNumerals)}
                </AppText>
                <AppText variant="micro" weight="medium" style={{ color: tone.fg, opacity: 0.85 }}>
                  {t('match.playerStats.rating')}
                </AppText>
              </View>
            ) : null}
          </View>

          <ScrollView contentContainerStyle={{ paddingBottom: 8 }}>
            {/* Headline stat grid */}
            <View className="px-5">
              <AppText
                variant="micro"
                weight="bold"
                className="mb-2 uppercase tracking-widest text-soft"
              >
                {t('match.playerStats.title')}
              </AppText>
              <View className="flex-row flex-wrap gap-2">
                {tiles.map((tile) => (
                  <StatCell key={tile.label} label={tile.label} value={tile.value} />
                ))}
              </View>
            </View>

            {/* Detail strip from raw — only when at least one value is present. */}
            {detailRows.length > 0 ? (
              <View className="mx-5 mt-4 rounded-card bg-surface-2/40 px-4 py-1">
                {detailRows.map((row, index) => (
                  <View
                    key={row.label}
                    className={index === 0 ? '' : 'border-t border-line'}
                  >
                    <DetailRow label={row.label} value={row.value} />
                  </View>
                ))}
              </View>
            ) : null}
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );
}
